import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { SERVICE_BY_KEY } from "@/lib/services";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Plus, ChatCircleDots, ArrowUpRight, TrendUp, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const { user, profile } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [billing, setBilling] = useState({ total_paid: 0, total_due: 0, invoice_count: 0 });
  const [loading, setLoading] = useState(true);
  const displayName = profile?.full_name || user?.user_metadata?.full_name || (user?.email || "").split("@")[0];
  const workspace = profile?.company || user?.user_metadata?.company || "Personal";

  useEffect(() => {
    Promise.all([
      api.get("/projects").then(({ data }) => setProjects(data)),
      api.get("/billing/summary").then(({ data }) => setBilling(data)),
    ]).catch(() => toast.error("Failed to load workspace")).finally(() => setLoading(false));
  }, []);

  const counts = projects.reduce((a, p) => { a[p.status] = (a[p.status]||0)+1; return a; }, {});
  const recent = projects.slice(0, 5);

  return (
    <AppShell
      subtitle={`◇ ${new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase()}`}
      title={<>Hello, <span className="text-[#0B4DCC]">{(displayName || "").split(" ")[0]}</span>.</>}
      actions={
        <>
          <Button onClick={() => nav("/dashboard/chat")} variant="outline" className="rounded-md border-black/20 h-10" data-testid="dash-open-chat">
            <ChatCircleDots size={14} weight="bold" className="mr-2" /> Ask Agenaix Assistant
          </Button>
          <Button onClick={() => nav("/dashboard/new")} className="fx-btn-primary rounded-md h-10" data-testid="dash-new-project">
            <Plus size={14} weight="bold" className="mr-2" /> New project
          </Button>
        </>
      }
    >
      {/* KPI GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-black/10 mb-10">
        {[
          { k: "ACTIVE", v: (counts.approved||0) + (counts.in_progress||0), sub: "in production" },
          { k: "PENDING REVIEW", v: counts.pending||0, sub: "awaiting scope" },
          { k: "COMPLETED", v: counts.completed||0, sub: "shipped" },
          { k: "AMOUNT DUE", v: formatCurrency(billing.total_due), sub: "across open invoices" },
        ].map(({k,v,sub}) => (
          <div key={k} className="border-r border-b border-black/10 p-6" data-testid={`dash-kpi-${k.toLowerCase().replace(/ /g,'-')}`}>
            <div className="fx-mono-tag">{k}</div>
            <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{v}</div>
            <div className="mt-2 text-xs text-neutral-500">{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent projects */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold tracking-tight">Recent projects</h2>
            <Link to="/dashboard/projects" className="fx-mono-tag hover:text-black" data-testid="dash-view-all-projects">VIEW ALL →</Link>
          </div>

          {loading ? (
            <div className="border border-black/10 p-10 text-center text-neutral-500">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="border border-dashed border-black/20 p-10 text-center">
              <div className="fx-mono-tag mb-2">EMPTY QUEUE</div>
              <h3 className="text-xl font-bold tracking-tight mb-2">No projects yet.</h3>
              <p className="text-neutral-600 mb-5 text-sm">Submit your first brief to get started.</p>
              <Button onClick={() => nav("/dashboard/new")} className="fx-btn-accent rounded-md h-10" data-testid="dash-empty-cta">Submit first project →</Button>
            </div>
          ) : (
            <div className="border border-black/10 divide-y divide-black/5">
              {recent.map(p => {
                const svc = SERVICE_BY_KEY[p.service_type];
                return (
                  <Link to={`/dashboard/projects/${p.id}`} key={p.id} className="p-5 flex items-center gap-4 hover:bg-[#F8FAFC] transition-colors" data-testid={`dash-recent-${p.id}`}>
                    <div className="fx-mono-tag w-12 shrink-0">{svc?.code}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm tracking-tight truncate">{p.title}</div>
                      <div className="text-xs text-neutral-500 mt-0.5 font-mono">{svc?.name} · {formatDate(p.created_at)}</div>
                    </div>
                    <StatusBadge status={p.status} />
                    <ArrowUpRight size={14} weight="bold" className="text-neutral-400" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="border border-black/10 p-6">
            <div className="fx-mono-tag mb-3 flex items-center gap-2"><TrendUp size={12} /> BILLING SNAPSHOT</div>
            <div className="text-3xl font-black tracking-tighter font-mono">{formatCurrency(billing.total_paid)}</div>
            <div className="text-xs text-neutral-500 mt-1">Lifetime spend across {billing.invoice_count} invoices</div>
            <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between text-sm">
              <span className="text-neutral-500">Outstanding</span>
              <span className="font-mono font-semibold">{formatCurrency(billing.total_due)}</span>
            </div>
            <Link to="/dashboard/billing"><Button variant="outline" className="w-full mt-4 rounded-md h-9 border-black/20" data-testid="dash-view-billing">View billing →</Button></Link>
          </div>

          <div className="border border-black/10 p-6 bg-[#0B1F3A] text-white">
            <div className="fx-mono-tag mb-3" style={{color:'#a3a3a3'}}>◇ AI CONSULTANT</div>
            <h3 className="text-xl font-bold tracking-tight">Not sure what to build?</h3>
            <p className="mt-2 text-sm text-neutral-400">Chat with Agenaix Assistant to scope your idea in minutes.</p>
            <Button onClick={() => nav("/dashboard/chat")} className="mt-4 rounded-md h-10 bg-white text-black hover:bg-neutral-200 w-full" data-testid="dash-chat-cta">
              Start conversation →
            </Button>
          </div>

          <div className="border border-[#DCE3ED] p-6 rounded-xl">
            <div className="fx-mono-tag mb-3 flex items-center gap-2"><CheckCircle size={12} /> ACCOUNT</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#64748B]">Status</span><span className="font-medium text-[#15803D]">Active</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Workspace</span><span className="font-medium">{workspace}</span></div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
